import { LitheSpecificElement } from '@wheelbox/lithejs-engine';
import { compileMarkdown } from '@wheelbox/lithejs-markdown';

export interface TutorialMeta {
  filename: string;
  title: string;
  order: number;
}

export interface TutorialData {
  title: string;
  code?: string;
  keywords: string[];
  content: LitheSpecificElement;
}

/**
 * Parse frontmatter from markdown content
 */
function parseFrontmatter(content: string): {
  frontmatter: Record<string, string | string[]>;
  body: string;
} {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);

  if (!match) {
    return { frontmatter: {}, body: content };
  }

  const [, frontmatterRaw, body] = match;
  const frontmatter: Record<string, string | string[]> = {};

  frontmatterRaw.split('\n').forEach((line) => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > -1) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Handle arrays like keywords: ["a", "b"]
      if (value.startsWith('[') && value.endsWith(']')) {
        try {
          frontmatter[key] = JSON.parse(value);
        } catch {
          frontmatter[key] = value;
        }
      } else {
        // Remove quotes if present
        if (
          (value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))
        ) {
          value = value.slice(1, -1);
        }
        frontmatter[key] = value;
      }
    }
  });

  return { frontmatter, body };
}

/**
 * Fetch tutorial index (list of all tutorials) with localized titles
 */
export async function fetchTutorialIndex(
  language: string = 'en'
): Promise<TutorialMeta[]> {
  const response = await fetch('/tutorials/index.json');
  if (!response.ok) {
    throw new Error(`Failed to fetch tutorial index: ${response.statusText}`);
  }
  const index: TutorialMeta[] = await response.json();

  // Fetch localized titles from markdown files
  const localizedIndex = await Promise.all(
    index.map(async (item) => {
      try {
        const mdResponse = await fetch(
          `/tutorials/${item.filename}_${language}.md`
        );
        if (mdResponse.ok) {
          const content = await mdResponse.text();
          const { frontmatter } = parseFrontmatter(content);
          return {
            ...item,
            title: (frontmatter.title as string) || item.title,
          };
        }
      } catch {
        // Ignore errors, use default title
      }
      return item;
    })
  );

  return localizedIndex;
}

/**
 * Fetch and parse a single tutorial by filename and language
 */
export async function fetchTutorial(
  filename: string,
  language: string = 'en'
): Promise<TutorialData> {
  const response = await fetch(`/tutorials/${filename}_${language}.md`);

  if (!response.ok) {
    // Fallback to English if language not available
    if (language !== 'en') {
      return fetchTutorial(filename, 'en');
    }
    throw new Error(`Failed to fetch tutorial: ${response.statusText}`);
  }

  const content = await response.text();
  const { frontmatter, body } = parseFrontmatter(content);

  return {
    title: (frontmatter.title as string) || filename,
    code: frontmatter.code as string | undefined,
    keywords: (frontmatter.keywords as string[]) || [],
    content: compileMarkdown(body, {
      wrapper: 'div',
      classes: 'tutorial-content',
    }) as LitheSpecificElement,
  };
}
